import * as React from 'react';
// mui
import {
  ListItemText,
  Typography,
} from '@mui/material';
// icons
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import AccessAlarmIcon from '@mui/icons-material/AccessAlarm';
import SubdirectoryArrowRightIcon from '@mui/icons-material/SubdirectoryArrowRight';
import FlagIcon from '@mui/icons-material/Flag';
import NotesIcon from '@mui/icons-material/Notes';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
// redux
import { useSelector } from 'react-redux';
import { selectTaskById, selectTasksByParentId } from '../../../store/selectors/taskSelectors';
import { selectEventsByParentId } from '../../../store/selectors/eventSelectors';
import { selectRecurringTasksByParentId } from '../../../store/selectors/recuringTaskSelectors';
import { selectProjectById } from '../../../store/selectors/projectSelectors';
// types
import Event from '../../../interfaces/Event.interface';
import Task from '../../../interfaces/Task.interface';
import RecurringTask from '../../../interfaces/RecurringTask.interface';
import Project from '../../../interfaces/Project.interface';
// components
import { convertEnergyToString, convertPriorityToString, convertTimeToMinutesString } from '../../common/propertyValueToString';
import ItemContentDate from '../../common/ItemContentDate';
import MiniTask from '../../../interfaces/MiniTask.interface';

interface TaskListItemContentProps {
  taskId: number;
  showProject?: boolean;
}

export default function TaskItemContent(props: TaskListItemContentProps) {
  // props
  const { taskId } = props;
  const showProject = props.showProject || false;

  // redux
  const localTask: Task = useSelector(selectTaskById(taskId)) as Task;
  const localSubtasks: Task[] = useSelector(selectTasksByParentId(localTask.id));
  const localEvents: Event[] = useSelector(selectEventsByParentId(localTask.id));
  const localRecurringTasks: RecurringTask[] = useSelector(selectRecurringTasksByParentId(localTask.id));
  const localProject: Project = useSelector(selectProjectById(localTask.projectId)) as Project;

  // local constants
  const completedFilter = (item: Task | Event | RecurringTask) => {
    return item.completed;
  }
  const completedItemsCount =
    localSubtasks.filter(completedFilter).length +
    localEvents.filter(completedFilter).length +
    localRecurringTasks.filter((task: RecurringTask) => task.permanentlyCompleted).length +
    localTask.miniTasks.filter((miniTask: MiniTask) => miniTask.completed).length;
  const totalItemsCount = localSubtasks.length + localEvents.length + localRecurringTasks.length + localTask.miniTasks.length;

  return (
    <ListItemText
      sx={{color: localTask.completed || localTask.excused ? 'grey' : 'inherit'}}
      primary={localTask.title}
      secondary={
        <Typography
          variant={'caption'}
          sx={{color: localTask.completed ? 'grey' : '#9e9e9e'}}
        >
          { totalItemsCount > 0 && // if there are subtasks display subtasks fraction
            <React.Fragment>
              <SubdirectoryArrowRightIcon
                sx={{ height: '18px', width: '18px', marginBottom: '-4px' }}
              />{`${completedItemsCount}/${totalItemsCount}`}
            </React.Fragment>
          }

          { localTask.description &&
            <NotesIcon sx={{
              height: '18px',
              width: '20px',
              marginBottom: '-4px',
              marginLeft: totalItemsCount > 0 ? '4px' : '',
              mr: '4px'
            }}/>
          }

          { localTask.dueDate &&
            <ItemContentDate
              itemData={localTask}
              itemType="task"
              padLeft={!!localTask.description || totalItemsCount > 0}
            />
          }

          {/* task info icons */}
          <FlagIcon sx={{
            height: '18px',
            width: '20px',
            color: localTask.priority,
            marginBottom: '-4px',
            marginLeft: localSubtasks.length > 0 || localTask.description || localTask.dueDate ? '4px' : ''
          }} />{convertPriorityToString(localTask.priority)} 

          <ElectricBoltIcon sx={{
            height: '18px',
            width: '20px',
            color: localTask.energy,
            marginBottom: '-4px',
            marginLeft: '4px'
          }} />{convertEnergyToString(localTask.energy)} 

          <AccessAlarmIcon sx={{
            height: '18px',
            width: '20px',
            color: localTask.time,
            marginBottom: '-4px',
            marginLeft: '8px'
          }} />{convertTimeToMinutesString(localTask.time)}

          { showProject && (
            <React.Fragment>
              <FolderOpenIcon sx={{
                height: '18px',
                width: '20px',
                marginBottom: '-4px',
                ml: '5px',
                mr: '1px',
              }}/>{localProject.name}
            </React.Fragment>
          )}
        </Typography>
      }
    />
  )
}